/* eslint-disable no-nested-ternary */
import {
  getReviewCyclesApi,
  getReviewRatingDetailsApi,
  getQuestionsApi,
  getQuestionByIdApi,
  saveQuestionApi,
  archivalReviewCycleApi,
  getReviewFormRatingDetailsApi,
  getReviewGoalsTimelineListApi,
  getQuestionsWithCustomDescriptionApi,
} from 'api/reviews';
import ReviewCycle from 'models/admin/reviewCycle';
import ReviewQuestion from 'models/reviewQuestion';
import ReviewRating from 'models/reviewRatingDetails';

import { QUESTION_FORM_SOURCES } from 'constants/ProjectConstants';

export const getReviewCycles = (include_draft = false) =>
  getReviewCyclesApi({ include_draft }).then((res) =>
    res.entity.map((json) => {
      const cycle = new ReviewCycle();
      cycle.fromJSON(json);
      return cycle;
    })
  );

export const archivalReviewCycle = ({ formId, isArchive }) => archivalReviewCycleApi({ formId, isArchive });

export const getReviewRatingDetails = ({
  reviewId,
  employeeId,
  providerId,
  isIndividualReviewResult = false,
  responseId,
  providerType,
  isPreview,
  previewSubmissionStatus,
  previewProviderType,
}) =>
  getReviewRatingDetailsApi({
    reviewId,
    employeeId,
    providerId,
    isIndividualReviewResult,
    responseId,
    providerType,
    isPreview,
    previewSubmissionStatus,
    previewProviderType,
  }).then((res) => {
    const data = ReviewRating.fromJSON(res.entity);
    return data;
  });

export const getReviewFormRatingDetails = ({ reviewId, employeeId, providerId }) =>
  getReviewFormRatingDetailsApi({
    reviewId,
    employeeId,
    providerId,
  }).then((res) => {
    const data = ReviewRating.fromJSON(res.entity);
    return data;
  });

export const getQuestions = ({ offset = 0, limit = 100, label, source = 'review' }) =>
  getQuestionsApi({ offset, limit, label, source }).then((res) =>
    res.entity.map((question) => ReviewQuestion.fromJSON(question))
  );

export const getQuestionsWithCustomDescription = ({ offset = 0, limit = 100, label, source = 'review' }) =>
  getQuestionsWithCustomDescriptionApi({ offset, limit, label, source }).then((res) =>
    res.entity.map((question) => ReviewQuestion.fromJSON(question))
  );

export const getQuestionById = ({ questionId }) =>
  getQuestionByIdApi({ questionId }).then((res) => {
    const data = ReviewQuestion.fromJSON(res.entity);
    return data;
  });

export const saveQuestion = ({ question, source, buttonType }) => {
  let question1 = {
    id: question.id || null,
    description: question.description,
    enableFeedbackText: question.enableFeedbackText,
    isArchived: question.isArchived,
    isTextRequired: question.isTextRequired,
    hasCustomDescription: question.hasCustomDescription,
    providerToDescription: question.providerToDescription,
    isArchive: question.isArchived,
  };

  if (source === 'review') {
    question1 = {
      ...question1,
      answerType: question.multiSelect
        ? 'MULTI_SELECT'
        : !question.enableFeedbackText && question.answerType === 'NONE'
          ? 'SHORT_TEXT'
          : question.answerType === 'MULTI_SELECT'
            ? 'DROP_DOWN'
            : question.answerType,
      questionType: question.questionType,
      tag:
        question.tagId && question.questionType === 'COMPETENCY'
          ? {
              uuid: question.tagId,
            }
          : null,
      minOptions:
        (question.allowRange || question.answerType === 'STAR_RATING' || question.answerType === 'NUMBER_RATING') &&
        question.minOptions
          ? Number(question.minOptions)
          : null,
      maxOptions:
        (question.allowRange || question.answerType === 'STAR_RATING' || question.answerType === 'NUMBER_RATING') &&
        question.maxOptions
          ? Number(question.maxOptions)
          : null,
      answerOptions:
        question.answerType === 'MULTI_SELECT' || question.answerType === 'DROP_DOWN'
          ? question.answerOptions
              .filter(
                (q) =>
                  (question.multiSelect || (q.score !== null && !Number.isNaN(q.score) && q.score >= 0)) &&
                  q.description
              )
              .map((q, i) => ({
                description: q.description,
                score: !question.multiSelect ? Number(q.score) : null,
                displayOrder: i + 1,
              }))
          : ['STAR_RATING'].includes(question.answerType)
            ? question.answerOptions.map((q, index) => ({
                description: q.description,
                displayOrder: index + 1,
                score: index + 1,
              }))
            : ['STAR_RATING', 'NUMBER_RATING'].includes(question.answerType)
              ? question.answerOptions
              : [],
    };
  } else if (source === 'engagement') {
    const isScoreMandatory =
      question.isScoreMandatory || (question.answerType !== 'DROP_DOWN' && question.answerType !== 'NONE');
    const isDescriptionRequired = question.answerType === 'DROP_DOWN' && question.answerType !== 'NONE';
    const answerOptions = question.answerOptions
      .filter(
        (q) =>
          (!isScoreMandatory || (q.score >= 0 && !Number.isNaN(q.score))) &&
          (isDescriptionRequired ? q.description : true)
      )
      .map((q, i) => ({
        description: q.description,
        score: isScoreMandatory ? Number(q.score) : null,
        displayOrder: i + 1,
      }));
    const sortedAnswerOptions = answerOptions.sort((a, b) => a.score - b.score);

    question1 = {
      ...question1,
      answerType: question.answerType,
      questionType: 'ENGAGEMENT',
      analyticsQuestionType: isScoreMandatory ? 'RATING' : null,
      tag: { uuid: question.tagId },
      minOptions: null,
      maxOptions:
        isScoreMandatory && sortedAnswerOptions.length ? sortedAnswerOptions[answerOptions.length - 1].score : null,
      answerOptions: question.answerType === 'NONE' ? [] : answerOptions,
    };
  } else if (source === QUESTION_FORM_SOURCES.ONE_ON_ONE) {
    const isScoreMandatory = question.isScoreMandatory || question.answerType !== 'DROP_DOWN';
    const answerOptions = question.answerOptions
      .filter((q) => (!isScoreMandatory || (q.score && !Number.isNaN(q.score))) && q.description)
      .map((q, i) => ({
        description: q.description,
        score: isScoreMandatory ? Number(q.score) : null,
        displayOrder: i + 1,
      }));

    question1 = {
      ...question1,
      answerType: question.answerType,
      questionType: question.questionType,
      analyticsQuestionType: null,
      tag: null,
      minOptions: null,
      maxOptions: null,
      answerOptions,
      formType: source.toUpperCase(),
    };
  }

  return saveQuestionApi({ question: question1, buttonType }).then((res) => res);
};

export const getReviewGoalsTimelineList = async ({ employeeId }) => {
  const res = await getReviewGoalsTimelineListApi({ employeeId });
  return res.entity;
};
